/**
 * Cart Drawer Handler
 * 1. Converts ALL /cart/add link clicks (memberships, reward packs, etc.) from
 *    page navigation to AJAX add-to-cart so the cart drawer opens.
 * 2. Prevents adding the same membership tier to cart twice.
 *    Instead of showing KORS error screen, shows a friendly notice in the drawer.
 *
 * Cart variant IDs are rendered server-side via Liquid (window.PCH_CART_VARIANT_IDS).
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'pch_membership_duplicate_msg';

  // Membership buttons (used for duplicate detection)
  var MEMBERSHIP_SELECTORS =
    'a.plans-v2-btn--nonmember[data-tier],' +
    'a.plans-v2-mcard__btn--nonmember[data-tier],' +
    'a.package-card__btn--nonmember[data-tier],' +
    'a.plans-table__btn--nonmember[data-tier],' +
    'a.plans-mobile-card__btn--nonmember[data-tier]';

  function getVariantIdFromUrl(url) {
    var match = url.match(/[?&]id=(\d+)/);
    return match ? match[1] : null;
  }

  function getSellingPlanFromUrl(url) {
    var match = url.match(/[?&]selling_plan=(\d+)/);
    return match ? match[1] : null;
  }

  function addToCartAjax(variantId, sellingPlan) {
    var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
    if (sellingPlan) {
      body.items[0].selling_plan = parseInt(sellingPlan, 10);
    }

    // Get section IDs for cart drawer update
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function(el) {
      if (el.dataset && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function(res) { return res.json(); });
  }

  function dispatchCartAddEvent(response) {
    // Dispatch the same event the theme's product form uses to trigger the cart drawer
    var event = new CustomEvent('cart:item-added', { bubbles: true });
    event.detail = { resource: response };
    document.dispatchEvent(event);
  }

  function showDrawerNotice(tierName) {
    // Remove any existing notice first
    var existing = document.querySelector('.pch-drawer-notice');
    if (existing) existing.remove();

    var target = document.querySelector('.cart-drawer__items');
    if (!target) return;

    var banner = document.createElement('div');
    banner.className = 'pch-drawer-notice';
    banner.style.cssText = 'padding: 12px 16px; margin: 0 16px 8px; background: #FFF3CD; border-radius: 8px; font-size: 13px; color: #333; display: flex; align-items: center; gap: 8px;';
    banner.innerHTML =
      '<span style="font-size: 16px;">&#9432;</span>' +
      '<span>Your <strong>' + tierName + '</strong> membership is already in your cart.</span>' +
      '<button style="margin-left: auto; background: none; border: none; font-size: 18px; cursor: pointer; padding: 0; line-height: 1;" aria-label="Dismiss">&times;</button>';

    function dismiss() {
      banner.style.transition = 'opacity 0.3s';
      banner.style.opacity = '0';
      setTimeout(function() { banner.remove(); }, 300);
    }

    banner.querySelector('button').addEventListener('click', dismiss);
    target.parentNode.insertBefore(banner, target);

    // Auto-dismiss after 5 seconds
    setTimeout(dismiss, 5000);
  }

  function init() {
    var cartVariantIds = window.PCH_CART_VARIANT_IDS || [];

    // Build lookup set
    var inCart = {};
    cartVariantIds.forEach(function(id) {
      inCart[String(id)] = true;
    });

    // Sync inCart with actual cart when items are removed/changed
    function syncInCart() {
      fetch('/cart.js', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          // Rebuild lookup from actual cart contents
          var fresh = {};
          (cart.items || []).forEach(function(item) {
            fresh[String(item.variant_id)] = true;
          });
          inCart = fresh;
        })
        .catch(function() { /* keep existing state on error */ });
    }

    // Theme dispatches 'cart:update' (no d), our code dispatches 'cart:updated'
    document.addEventListener('cart:update', syncInCart);
    document.addEventListener('cart:updated', syncInCart);

    document.addEventListener('click', function(e) {
      // Match any <a> linking to /cart/add
      var link = e.target.closest('a[href*="/cart/add"]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      if (href.indexOf('/cart/add') === -1) return;

      var variantId = getVariantIdFromUrl(href);
      if (!variantId) return;

      // Always prevent default navigation — we handle everything via AJAX
      e.preventDefault();
      e.stopPropagation();

      // Check if this is a membership button (for duplicate detection)
      var isMembership = link.matches(MEMBERSHIP_SELECTORS);

      // Duplicate membership — show message in drawer or fall back to cart page
      if (isMembership && inCart[variantId]) {
        var tierName = link.getAttribute('data-tier') || 'this membership';
        var displayName = tierName.charAt(0).toUpperCase() + tierName.slice(1);

        var cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          // Open the drawer and show the duplicate notice inside it
          cartDrawer.open();
          setTimeout(function() {
            showDrawerNotice(displayName);
          }, 300);
        } else {
          // No drawer — fall back to cart page with message
          sessionStorage.setItem(STORAGE_KEY, displayName);
          window.location.href = '/cart';
        }
        return;
      }

      // Not a duplicate — AJAX add to cart, then open drawer
      var sellingPlan = getSellingPlanFromUrl(href);

      // Disable button to prevent double-clicks
      link.style.pointerEvents = 'none';
      link.style.opacity = '0.6';

      addToCartAjax(variantId, sellingPlan).then(function(response) {
        if (response.status) {
          // Error from Shopify (e.g. out of stock) — fall back to page navigation
          window.location.href = href;
          return;
        }

        // Track this variant as now in cart
        inCart[variantId] = true;

        // Try to open the cart drawer via the theme's event system
        var cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          // Refresh the drawer contents first
          var cartItemsComponent = document.querySelector('cart-items-component');
          if (cartItemsComponent) {
            // Fetch fresh cart HTML for the drawer sections
            var sectionId = cartItemsComponent.dataset.sectionId;
            if (sectionId) {
              fetch('/?sections=' + sectionId)
                .then(function(r) { return r.json(); })
                .then(function(sections) {
                  if (sections[sectionId]) {
                    var temp = document.createElement('div');
                    temp.innerHTML = sections[sectionId];
                    var newContent = temp.querySelector('cart-items-component');
                    if (newContent) {
                      cartItemsComponent.innerHTML = newContent.innerHTML;
                    }
                  }
                  cartDrawer.open();
                  document.dispatchEvent(new CustomEvent('cart:updated'));
                })
                .catch(function() {
                  cartDrawer.open();
                  document.dispatchEvent(new CustomEvent('cart:updated'));
                });
            } else {
              cartDrawer.open();
            }
          } else {
            cartDrawer.open();
          }
        } else {
          // No cart drawer — fall back to cart page
          window.location.href = '/cart';
        }
      }).catch(function() {
        // Network error — fall back to page navigation
        window.location.href = href;
      }).finally(function() {
        link.style.pointerEvents = '';
        link.style.opacity = '';
      });
    }, true);
  }

  // Cart page: show duplicate message if one was set
  function showCartMessage() {
    if (window.location.pathname !== '/cart') return;

    var tierName = sessionStorage.getItem(STORAGE_KEY);
    if (!tierName) return;
    sessionStorage.removeItem(STORAGE_KEY);

    function inject() {
      var cartTitle = document.querySelector('.cart-page__title');
      if (!cartTitle) return;

      var banner = document.createElement('div');
      banner.className = 'pch-cart-notice';
      banner.innerHTML =
        '<div class="pch-cart-notice__content">' +
          '<span class="pch-cart-notice__icon">&#9432;</span>' +
          '<span class="pch-cart-notice__text">' +
            'Your <strong>' + tierName + '</strong> membership is already in your cart. ' +
            'Only one of each membership tier can be purchased at a time.' +
          '</span>' +
          '<button class="pch-cart-notice__close" aria-label="Dismiss">&times;</button>' +
        '</div>';

      function dismissBanner() {
        banner.style.animation = 'pchNoticeOut 0.3s ease forwards';
        setTimeout(function() { banner.remove(); }, 300);
      }

      banner.querySelector('.pch-cart-notice__close').addEventListener('click', dismissBanner);

      cartTitle.parentNode.insertBefore(banner, cartTitle.nextSibling);

      // Auto-fade after 5 seconds
      setTimeout(dismissBanner, 5000);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', inject);
    } else {
      inject();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  showCartMessage();
})();
